import { filter, find, first, map, upperFirst } from "lodash";
import { Person } from "@/features/people/graphql/generated";
import { DimensionWithScores } from "@/features/people";
import { TFunction } from "i18next";
import { DimensionInsightParams } from "./Insight.types";
import { CompetencyInsight } from "./useCompetencyInsights";
import { DimensionContext } from "@/types/data";
import { getHighligthighContext } from "./getValuesInsight";

/**
 * Parameters specific to competency insights (extends base with context)
 */
export interface CompetencyInsightParams extends DimensionInsightParams {
  context: DimensionContext;
}

/**
 * Helper to get context name for translations
 */
function getContextName(
  context: DimensionContext,
  person: Person | undefined,
  t: TFunction
): string {
  if (person?.first_name) return person.first_name;
  return context === "team"
    ? t("competency_insights.context_team")
    : t("competency_insights.context_they");
}

/**
 * Helper to get people associated with a dimension for a specific person
 */
function getInsightPeople(
  dimension: DimensionWithScores,
  person: Person | undefined
): Person[] {
  if (person) {
    return map(
      filter(dimension.peopleScores, (ps) => ps.person?.id === person.id),
      (ps) => ps.person as Person
    ).filter(Boolean);
  }

  return map(dimension.peopleScores, (ps) => ps.person).filter(
    Boolean
  ) as Person[];
}

/**
 * Creates a likely strength insight for a single dimension (score >= 70)
 * Returns null if dimension doesn't qualify for a likely strength insight
 */
export function getLikelyStrengthInsight(
  dimension: DimensionWithScores,
  params: CompetencyInsightParams,
  t: TFunction
): CompetencyInsight | null {
  const score = dimension.score?.value || 0;

  if (score < 70) {
    return null;
  }

  // Only child dimensions (with parentId) qualify
  if (dimension.parentId === null) {
    return null;
  }

  const { context, person } = params;
  const members = getInsightPeople(dimension, person);

  const highlightContext = getHighligthighContext(
    context,
    person || first(members),
    t
  );

  return {
    dimension,
    type: "likely_strengths",
    tags: [
      {
        label: t("competency_insights.likely_strengths"),
        color: "text-green-500",
        icon: "Strength",
        bg: "bg-green-500",
      },
    ],
    title: t("competency_insights.top_strength_title", {
      dimensionName: dimension.title,
    }),
    subtitle: "",
    description: t("competency_insights.top_strength_description", {
      context: context === "user" ? "" : context,
      dimensionName: dimension.title,
    }),
    highlight: {
      color: "bg-green-100",
      title: t("competency_insights.likely_strengths_highlight_title", {
        dimensionName: dimension.title,
        context: highlightContext,
      }),
      // upperFirst here because the highlight variable is in the beginning of the sentence
      description: upperFirst(
        t("competency_insights.likely_strengths_highlight_description", {
          context: highlightContext,
          dimensionName: dimension.title,
        })
      ),
      tag: {
        label: t("competency_insights.likely_strengths"),
        color: "text-green-500",
        icon: "Strength",
        bg: "bg-green-500",
      },
    },
    people: members,
  };
}

/**
 * Creates a development potential insight for a single dimension (score <= 30)
 * Returns null if dimension doesn't qualify for a development potential insight
 */
export function getDevelopmentPotentialInsight(
  dimension: DimensionWithScores,
  params: CompetencyInsightParams,
  t: TFunction
): CompetencyInsight | null {
  const score = dimension.score?.value || 0;

  if (score > 30) {
    return null;
  }

  // Only child dimensions (with parentId) qualify
  if (dimension.parentId === null) {
    return null;
  }

  const { context, person } = params;
  const members = getInsightPeople(dimension, person);
  const highlightContext = getHighligthighContext(
    context,
    person || first(members),
    t
  );

  return {
    dimension,
    type: "development_potential",
    tags: [
      {
        label: t("competency_insights.development_potential"),
        color: "text-orange-500",
        icon: "Plant",
        bg: "bg-orange-500",
      },
    ],
    title: t("competency_insights.bottom_skill_title", {
      dimensionName: dimension.title,
      context: highlightContext,
    }),
    subtitle: "",
    description: t("competency_insights.bottom_skill_description", {
      context: context === "user" ? "" : context,
      dimensionName: dimension.title,
    }),
    highlight: {
      color: "bg-orange-100",
      title: upperFirst(
        t("competency_insights.development_potential_highlight_title", {
          dimensionName: dimension.title,
          context: highlightContext,
        })
      ),
      description: t(
        "competency_insights.development_potential_highlight_description",
        {
          context: highlightContext,
          dimensionName: dimension.title,
        }
      ),
      tag: {
        label: t("competency_insights.development_potential"),
        color: "text-orange-500",
        icon: "Plant",
        bg: "bg-orange-500",
      },
    },
    people: members,
  };
}

/**
 * Type for member with score diff information
 */
interface MemberWithDiff extends Person {
  diff: number;
  userScore: number;
}

/**
 * Creates a unique skills insight for a single dimension (team context only)
 * Returns null if dimension doesn't qualify for a unique skills insight
 */
export function getUniqueSkillsInsight(
  dimension: DimensionWithScores,
  params: CompetencyInsightParams,
  t: TFunction
): CompetencyInsight | null {
  const { context, person } = params;

  // Unique skills only applies to team context
  if (context === "user" || context === "person") {
    return null;
  }

  // Only child dimensions (with parentId) qualify
  if (dimension.parentId === null) {
    return null;
  }

  const avgScore = dimension.averageScore?.value || 0;
  const stdev = find(dimension.allScores, { key: "stdev" })?.value || 0;

  const membersScores = person
    ? filter(dimension.peopleScores, (ps) => ps.person?.id === person.id)
    : dimension.peopleScores;

  const topMembers = membersScores
    ?.map((personScore) => {
      const userScore = personScore.value || 0;
      const diff = userScore - avgScore;
      return {
        ...personScore.person,
        diff,
        userScore,
      };
    })
    .filter(
      (member): member is MemberWithDiff =>
        member && member.diff >= 15 && stdev < 25 && member.userScore > 60
    );

  if (!topMembers || topMembers.length === 0) {
    return null;
  }

  const auxVerb = topMembers.length > 1 ? "have" : "has";

  const memberNames =
    topMembers.length > 1
      ? `${[...topMembers]
          .reverse()
          .slice(1)
          .reverse()
          .map((m) => m.first_name)
          .join(", ")} and ${topMembers[topMembers.length - 1].first_name}`
      : topMembers.map((m) => m.first_name).join(" and ");

  return {
    dimension,
    type: "unique_skills",
    tags: [
      {
        label: t("competency_insights.unique_skills"),
        color: "text-blue-500",
        icon: "Value",
        bg: "bg-blue-500",
      },
    ],
    title: t("competency_insights.skills_development_title", {
      memberNames,
      auxVerb,
      dimensionName: dimension.title,
    }),
    subtitle: "",
    description: t("competency_insights.skills_development_description", {
      context,
      memberNames,
    }),
    highlight: {
      color: "bg-purple-100",
      title: t("competency_insights.skills_development_highlight_title", {
        dimensionName: dimension.title,
        context: person?.first_name || memberNames,
      }),
      description: t(
        "competency_insights.skills_development_highlight_description",
        {
          context,
          dimensionName: dimension.title,
        }
      ),
      tag: {
        label: t("competency_insights.unique_skills"),
        color: "text-purple-500",
        icon: "Value",
        bg: "bg-purple-500",
      },
    },
    people: topMembers,
  };
}

/**
 * Returns a single insight for a competency dimension
 * Automatically determines whether to return likely strength, development potential, or unique skills
 * Returns null if dimension doesn't qualify for any insight
 */
export function getCompetencyInsight(
  dimension: DimensionWithScores,
  params: CompetencyInsightParams,
  t: TFunction
): CompetencyInsight | null {
  // Try unique skills first (only for team context)
  const uniqueSkillsInsight = getUniqueSkillsInsight(dimension, params, t);
  if (uniqueSkillsInsight) {
    return uniqueSkillsInsight;
  }

  // Try likely strength
  const likelyStrengthInsight = getLikelyStrengthInsight(dimension, params, t);
  if (likelyStrengthInsight) {
    return likelyStrengthInsight;
  }

  // Try development potential
  const developmentPotentialInsight = getDevelopmentPotentialInsight(
    dimension,
    params,
    t
  );
  if (developmentPotentialInsight) {
    return developmentPotentialInsight;
  }

  return null;
}
